/**
 * schemaUtils.js — Utilities for working with JSON Schema + x-extensions.
 *
 * Reads JSON Schema definitions with custom x- properties to determine
 * how to render, validate, and format document data in the editor.
 */

/**
 * Humanize a snake_case or camelCase field name into a display label.
 * Used as fallback when x-display-label is not defined in the schema.
 */
export function humanizeFieldName(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bId\b/, 'ID')
    .replace(/\bUrl\b/, 'URL')
    .replace(/\bSsn\b/, 'SSN');
}

/**
 * Get the display label for a field from its schema definition.
 */
export function getFieldLabel(fieldSchema, fieldKey) {
  return fieldSchema?.['x-display-label'] || humanizeFieldName(fieldKey);
}

/**
 * Get the display order for a field. Lower = first. Default 50.
 */
export function getFieldOrder(fieldSchema) {
  return fieldSchema?.['x-display-order'] ?? 50;
}

/**
 * Check if a field should be hidden from the editor.
 */
export function isFieldInternal(fieldSchema) {
  return fieldSchema?.['x-internal'] === true;
}

/**
 * Check if a field value should be masked by default.
 */
export function isFieldMasked(fieldSchema) {
  return fieldSchema?.['x-masked'] === true;
}

/**
 * Get the display format hint for a field (currency, date, etc.).
 */
export function getFieldFormat(fieldSchema) {
  if (fieldSchema?.['x-format']) return fieldSchema['x-format'];
  if (fieldSchema?.format === 'date') return 'date';
  return null;
}

/**
 * Check if a field links to a PDF page.
 */
export function isPageLink(fieldSchema) {
  return fieldSchema?.['x-page-link'] === true;
}

/**
 * Check if a field is read-only (not editable in the UI).
 */
export function isFieldReadOnly(fieldSchema) {
  return fieldSchema?.['x-read-only'] === true || fieldSchema?.readOnly === true;
}

/**
 * Get the overlay type declared on an array node.
 */
export function getOverlayType(schemaNode) {
  return schemaNode?.['x-overlay'] || null;
}

/**
 * Resolve the label template for an object node (e.g., "{account_type} *{account_number_last4}").
 * Optionally accepts the object schema to apply formatters (dates, currency) to template values.
 */
export function resolveLabel(template, data, objectSchema) {
  if (!template || !data) return '';
  const props = objectSchema?.properties || {};
  const result = template.replace(/\{(\w+)\}/g, (_, key) => {
    const val = data[key];
    if (val === undefined || val === null || val === '') return '';
    const fieldSchema = props[key];
    if (fieldSchema) {
      const fmt = getFieldFormat(fieldSchema);
      if (fmt === 'date') {
        // Format ISO date to M/D/YYYY for display
        const match = String(val).match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (match) return `${parseInt(match[2])}/${parseInt(match[3])}/${match[1]}`;
      }
    }
    return String(val);
  });
  // Clean up extra whitespace from empty template values
  return result.replace(/\s+/g, ' ').trim();
}

/**
 * Resolve a label using the primary `x-label-template`, falling back to
 * `x-label-template-fallback` if the primary template renders nothing
 * meaningful (e.g. when a no-account-number card statement leaves the
 * `{account_number_last4}` slot empty and the result is a stray "*").
 *
 * "Meaningful" here means: at least one alphanumeric character remains after
 * the template was applied. This is intentionally permissive — a label like
 * "*1234" passes (has digits), while "*" or "" does not.
 */
export function resolveLabelWithFallback(objectSchema, data) {
  if (!objectSchema || !data) return '';
  const primary = objectSchema['x-label-template'];
  const primaryResult = primary ? resolveLabel(primary, data, objectSchema) : '';
  if (primaryResult && /[A-Za-z0-9]/.test(primaryResult)) {
    return primaryResult;
  }
  const fallback = objectSchema['x-label-template-fallback'];
  if (fallback) {
    const fallbackResult = resolveLabel(fallback, data, objectSchema);
    if (fallbackResult && /[A-Za-z0-9]/.test(fallbackResult)) {
      return fallbackResult;
    }
  }
  return primaryResult || '';
}

/**
 * Get the JSON type(s) for a field. Handles type arrays like ["string", "null"].
 */
export function getFieldType(fieldSchema) {
  if (!fieldSchema?.type) return 'string';
  if (Array.isArray(fieldSchema.type)) {
    return fieldSchema.type.find(t => t !== 'null') || 'string';
  }
  return fieldSchema.type;
}

/**
 * Get enum values for a field, if defined. A `const` is treated as a
 * single-value enum so a fixed schema value (e.g. credit-card account_type
 * const "CREDITCARD") still renders as a labeled, schema-driven dropdown
 * rather than a free-text input — consistent with the enum doc types.
 */
export function getFieldEnum(fieldSchema) {
  if (fieldSchema?.enum) return fieldSchema.enum;
  if (fieldSchema?.const !== undefined) return [fieldSchema.const];
  return null;
}

/**
 * Get display label for an enum value. Falls back to humanized value.
 */
export function getEnumLabel(fieldSchema, value) {
  const labels = fieldSchema?.['x-enum-labels'];
  if (labels && labels[value]) return labels[value];
  return humanizeFieldName(String(value));
}

/**
 * Get the properties of an object schema, sorted by x-display-order.
 * Returns [{ key, schema, label, order, internal }] array.
 */
export function getSortedProperties(objectSchema) {
  const props = objectSchema?.properties || {};
  return Object.entries(props)
    .map(([key, schema]) => ({
      key,
      schema,
      label: getFieldLabel(schema, key),
      order: getFieldOrder(schema),
      internal: isFieldInternal(schema),
    }))
    .sort((a, b) => a.order - b.order);
}

/**
 * Separate properties into scalar fields, array fields, and object fields.
 * Scalar fields are rendered as key-value pairs.
 * Array and object fields are rendered as nested components.
 */
export function categorizeProperties(objectSchema) {
  const sorted = getSortedProperties(objectSchema);
  const scalars = [];
  const arrays = [];
  const objects = [];

  for (const prop of sorted) {
    if (prop.internal) continue;
    const type = getFieldType(prop.schema);
    if (type === 'array') {
      arrays.push(prop);
    } else if (type === 'object') {
      objects.push(prop);
    } else {
      scalars.push(prop);
    }
  }

  return { scalars, arrays, objects };
}

/**
 * Get the items schema for an array field.
 */
export function getArrayItemSchema(arraySchema) {
  return arraySchema?.items || {};
}

/**
 * Check if an object schema has nested arrays or objects (non-leaf).
 * Used to decide whether to render array items as collapsible nodes vs flat table rows.
 */
export function hasNestedChildren(objectSchema) {
  const props = objectSchema?.properties || {};
  for (const [, fieldSchema] of Object.entries(props)) {
    if (isFieldInternal(fieldSchema)) continue;
    const type = getFieldType(fieldSchema);
    if (type === 'array' || type === 'object') return true;
  }
  return false;
}

/**
 * Build column definitions from an array's item schema.
 * Returns columns sorted by x-display-order, excluding internal fields.
 */
export function getTableColumns(arraySchema) {
  const itemSchema = getArrayItemSchema(arraySchema);
  if (getFieldType(itemSchema) !== 'object') return [];

  return getSortedProperties(itemSchema)
    .filter(p => !p.internal && getFieldType(p.schema) !== 'array' && getFieldType(p.schema) !== 'object');
}

/**
 * Create a default empty object based on a schema's properties.
 * Uses schema defaults where defined, otherwise empty values by type.
 */
export function createDefaultObject(objectSchema) {
  const props = objectSchema?.properties || {};
  const obj = {};

  for (const [key, fieldSchema] of Object.entries(props)) {
    if (fieldSchema.const !== undefined) {
      // JSON Schema const: field must always have this exact value
      obj[key] = fieldSchema.const;
    } else if (fieldSchema.default !== undefined) {
      obj[key] = fieldSchema.default;
    } else {
      const type = getFieldType(fieldSchema);
      switch (type) {
        case 'string': obj[key] = ''; break;
        case 'number':
        case 'integer': obj[key] = 0; break;
        case 'boolean': obj[key] = false; break;
        case 'array': obj[key] = []; break;
        case 'object': obj[key] = {}; break;
        default: obj[key] = null;
      }
    }
  }

  // Assign a temporary ID if the schema has an id field
  if ('id' in props) {
    obj.id = `new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  return obj;
}

/**
 * Mask a string value for display (e.g., "123456789" -> "****6789").
 */
export function maskValue(value) {
  if (!value) return '****';
  const str = String(value);
  if (str.length <= 4) return str;
  return '*'.repeat(str.length - 4) + str.slice(-4);
}

/* ─────────────────────────────────────────────────────────────────────
 * Doc-type generalization conventions (foundation work — used by stage
 * code as it gets refactored). See doctype_registry.py and
 * neo4j-accounting-model.md for the full picture.
 * ──────────────────────────────────────────────────────────────────── */

/** Doc type identifier from the root schema (`x-document-type`). */
export function getDocumentType(rootSchema) {
  return rootSchema?.['x-document-type'] || null;
}

/** Schema version from the root schema (`x-version`). */
export function getSchemaVersion(rootSchema) {
  return rootSchema?.['x-version'] || null;
}

/**
 * Archetype declared at any node (`x-archetype`). Hybrid docs declare
 * archetypes at section nodes — walk parent-to-child and use the most
 * specific archetype found. Returns null if none declared.
 */
export function getSchemaArchetype(schemaNode) {
  return schemaNode?.['x-archetype'] || null;
}

/**
 * True if this array node should have bounding-box extraction applied
 * to its items (`x-bbox-target: true`). Decided per-array at schema
 * design time — not implied by archetype. A W2 schema can mark its box
 * fields bbox-able; a bank statement marks transactions.
 */
export function isBboxTarget(arraySchema) {
  return arraySchema?.['x-bbox-target'] === true;
}

/**
 * Walk a schema and collect every array node with `x-bbox-target: true`.
 * Returns [{ path, schema }] where path is a JSON-pointer-style array
 * of keys/indices, used by stage code to traverse the data structure.
 */
export function findBboxTargets(rootSchema) {
  const targets = [];
  const visit = (node, path) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'array' && isBboxTarget(node)) {
      targets.push({ path, schema: node });
    }
    if (node.properties) {
      for (const [k, v] of Object.entries(node.properties)) {
        visit(v, [...path, k]);
      }
    }
    if (node.items) {
      visit(node.items, [...path, '[]']);
    }
  };
  visit(rootSchema, []);
  return targets;
}
